import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { config } from '@app/core/app-config';
import { map } from 'rxjs/operators';
import { Filter } from '@app/shared/models/filter';
import { FilterService } from '@app/shared/services/filter/filter.service';

export interface BillingCycle {
  key: number;
  label: string;
  invoice_id?: string;
}

export interface Property {
  property_id: number;
  name: string;
  picture: string; // url
  thumbnail: string; // url
  status: string;
  muted?: boolean;
  guest_list: [
    {
      name: string;
      picture: string;
      check_in_date: string; //formatted already
    },
  ];
}

export interface PropertiesPagination {
  current_page: number;
  last_page: number;
  path: string;
  per_page: number;
  total: number;
}

export interface ActivePropertiesTotals {
  free: number;
  active: number;
}

export interface FetchPropertiesApiResponse {
  data: Property[];
  meta: {
    totals: ActivePropertiesTotals;
    pagination: PropertiesPagination;
  };
}

export interface FetchSummaryApiResponse {
  data: ActiveListingCycleSummary;
  meta: {
    uses_properties: boolean;
  };
}

export interface ActiveListingCycleSummary {
  total_amount: string;
  is_current: boolean;
  average_rate_per_listing: string;
  charge_date: string;
  currency: string;
  last_updated: string; // ISO 8601 date
}
@Injectable({
  providedIn: 'root',
})
export class ActivePropertiesService {
  private getBillingCyclesUrl = `${config.API_URL}/billing/active-properties`;

  constructor(
    private http: HttpClient,
    private filterService: FilterService
  ) {}

  fetchActivePropertyCycleSummary(timestampKey: number): Observable<{
    data: ActiveListingCycleSummary;
    meta: { uses_properties: boolean };
  }> {
    return this.http.get(`${this.getBillingCyclesUrl}/${timestampKey}/summary`).pipe(
      map((res: FetchSummaryApiResponse) => {
        return res;
      })
    );
  }

  fetchBillingCycles(): Observable<BillingCycle[]> {
    return this.http.get(this.getBillingCyclesUrl).pipe(map((res: { data: BillingCycle[] }) => res.data));
  }

  fetchProperties(
    timestampKey: number,
    query: string,
    pageNumber: number,
    filters: Filter[]
  ): Observable<FetchPropertiesApiResponse> {
    let url = `${this.getBillingCyclesUrl}/${timestampKey}`;

    const apiFilters = this.filterService.transformFilters(filters);

    const payload = {
      filters: apiFilters,
    };

    if (pageNumber) {
      url += `?page=${pageNumber}`;
    }

    if (query) {
      url += `&query=${query}`;
    }

    return this.http.post(url, payload).pipe(
      map((res: FetchPropertiesApiResponse) => {
        return res;
      })
    );
  }
}
